// Singleton wrapper for the netcountable-e2ee (NC1 key layer) API — same
// shape as SurveyApi/AccountApi. Register it in src/lib/auth.js
// _applyCredentials (E2eeApi.inited = false) so it picks up fresh SigV4
// credentials after every token refresh.

import e2eeClientFactory from './apigClient'
import AWSClockSkewHandler from '../AWSClockSkewHandler'
import store from "@/store"

class E2eeApi {
  constructor() {
    this.inited = false
    this.client = null
  }

  // The env var is a placeholder until the netcountable-e2ee service is
  // deployed for the stage. Callers (the signup hook) must check this and
  // no-op — never break signup because the key layer isn't reachable.
  isConfigured() {
    return !!process.env.VUE_APP_E2EE_URL
  }

  init() {
    if(!this.inited) {
      if(!this.isConfigured()) {
        throw new Error('VUE_APP_E2EE_URL is not set for this stage')
      }
      this.inited = true
      this.client = e2eeClientFactory.newClient(store.state.credentials, process.env.VUE_APP_E2EE_URL)
    }
  }

  async _call(fn) {
    this.init()

    let error
    let result = await AWSClockSkewHandler.handleClockSkew(fn).catch(err => {
      error = err
    })
    if(error) throw error

    return result
  }

  // ---- account ----

  async getAccount(email) {
    return await this._call(() => this.client.e2eeAccountGet({ email }))
  }

  /** payload = { email, identityBundle, deviceList, memberList, adkEnvelopes, log } — one validated transaction. */
  async bootstrapAccount(payload) {
    return await this._call(() => this.client.e2eeAccountPost({ email: payload.email }, payload))
  }

  /**
   * Finding ⑯: viewer-only identity reset — same payload shape as the
   * bootstrap but at identityVersion old+1; the service re-genesises the
   * partition. 409 for accounts with devices or other members.
   */
  async resetAccountIdentity(payload) {
    return await this._call(() => this.client.e2eeAccountResetPost({ email: payload.email }, payload))
  }

  /** §8.5 invites: email -> PUBLIC identity keys ({userId, idEncPk, idSignPk}); 404 = no NC1 identity. */
  async resolveIdentity(email) {
    return await this._call(() => this.client.e2eeResolveGet({ email }))
  }

  // ---- key documents ----

  async getIdentityBundle(email) {
    return await this._call(() => this.client.e2eeIdentityBundleGet({ email }))
  }

  /** sig = detached identity signature over the canonical bundle (base64). */
  async putIdentityBundle(email, value, sig) {
    return await this._call(() => this.client.e2eeIdentityBundlePost({ email }, { email, value, sig }))
  }

  async getDeviceList(email) {
    return await this._call(() => this.client.e2eeDeviceListGet({ email }))
  }

  /** Chain-committed write: logEntry lands transactionally with the doc. */
  async putDeviceList(email, value, logEntry) {
    return await this._call(() => this.client.e2eeDeviceListPost({ email }, { email, value, logEntry }))
  }

  async getMemberList(email) {
    return await this._call(() => this.client.e2eeMemberListGet({ email }))
  }

  async putMemberList(email, value, logEntry) {
    return await this._call(() => this.client.e2eeMemberListPost({ email }, { email, value, logEntry }))
  }

  async getAdkEnvelopes(email) {
    return await this._call(() => this.client.e2eeAdkEnvelopesGet({ email }))
  }

  /** Chunk 7: custody-signed full-set replacement of the rotation deliveries. */
  async putIdentityDeliveries(email, value, sig) {
    return await this._call(() => this.client.e2eeIdentityDeliveryPost({ email }, { email, value, sig }))
  }

  /** Chunk 7: permanently purge records before keepFromMs (custody-signed). */
  async purgeRecords(email, keepFromMs, sig) {
    return await this._call(() => this.client.e2eeRecordsPurgePost({ email }, { email, keepFromMs, sig }))
  }

  async putAdkEnvelopes(email, value, sig) {
    return await this._call(() => this.client.e2eeAdkEnvelopesPost({ email }, { email, value, sig }))
  }

  // ---- device-transfer enrollments (chunk 2) ----

  /** payload = { email, deviceUuid, deviceName, purpose, transferPk, devSignPk, devEncPk, fingerprint } */
  async createEnrollment(payload) {
    return await this._call(() => this.client.e2eeEnrollmentsPost({ email: payload.email }, payload))
  }

  /** Approver list: every live request for the account (no sealed bundles). */
  async listEnrollments(email) {
    return await this._call(() => this.client.e2eeEnrollmentsListGet({ email }))
  }

  /** Requester poll: state + sealedBundle once granted (+ deviceList/adkEnvelopes for uploaders). */
  async pollEnrollment(email, deviceUuid) {
    return await this._call(() => this.client.e2eeEnrollmentsPollGet({ email, deviceUuid }))
  }

  /**
   * body = { email, sealedBundle, sig } (viewer) or
   *        { email, sealedBundle, sig, deviceList, logEntry } (uploader);
   * sig = detached custody signature over the {requestId, transferPk,
   * sealedBundle} binding.
   */
  async acceptEnrollment(email, requestId, body) {
    return await this._call(() => this.client.e2eeEnrollmentAcceptPost({ email, requestId }, body))
  }

  async rejectEnrollment(email, requestId) {
    return await this._call(() => this.client.e2eeEnrollmentRejectPost({ email, requestId }, { email }))
  }

  // ---- transparency log ----

  async getLog(email, from) {
    let params = { email }
    if(from !== undefined && from !== null) {
      params.from = from
    }
    return await this._call(() => this.client.e2eeLogGet(params))
  }

  async appendLog(email, entry) {
    return await this._call(() => this.client.e2eeLogPost({ email }, { email, value: entry }))
  }
}

export default (new E2eeApi())
